<script setup lang="ts">
import { getMaxPoint, getDisplayData } from '@/services/functions/every24HoursChart';
import ToolTip from '@/components/Every24HoursReportPreviewParts/chart/ToolTip.vue';
import { onMounted, onBeforeUnmount, ref, watch, computed } from 'vue';
import { MARKER_SIZE } from '@/services/consts/every_24_hours_report';
import { formatDateTimeWithMilliseconds } from '@/services/functions';
import * as echarts from 'echarts';
import i18n from '@/lang/i18n';

const props = withDefaults(
  defineProps<{
    chartData: number[][]
    displayData: number[][]
    type: string
    scaleMinMax: number[]
    options: {
      grid: {
        left: string | number,
        top: string | number,
        bottom: string | number,
        width: string | number,
        height: string | number,
      },
      yAxis: {
        min: number,
        max: number,
        interval: number,
      }
    }
    verticalLines: { xAxis: number | null }[]
    chartHeight: string
  }>(),
  {
    chartHeight: '300',
  }
)

const chartRef = ref(null)
const chartInstance = ref<echarts.ECharts | null>(null)
const tooltip = ref({
  visible: false,
  x: 0,
  y: 0,
  time: '',
  value: ''
})
const EChartSetting = {
  renderer: 'canvas',
  useDirtyRect: true,
}
let tooltipEventRegistered = false;

onMounted( async() => {
  window.addEventListener('resize', resizeChart)
  initChart()
  makeTooltipEvent()
})

const maxPoint = computed(() => {
  if (props.type !== 'speed') {
    return undefined;
  }
  return getMaxPoint(props.displayData)
})

function getChartOptions() {
  return {
    animation: false,
    animationDuration: 0,
    animationEasing: 'linear',
    title: {
      show: false,
    },
    tooltip: {
      show: true,
      renderMode: 'html',
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          show: false
        }
      },
    },
    grid: {
      left: props.options.grid.left,
      top: props.options.grid.top,
      bottom: props.options.grid.bottom,
      width: props.options.grid.width,
      height: props.options.grid.height,
    },
    xAxis: {
      show: false,
      type: 'time',
      min: props.scaleMinMax[0],
      max: props.scaleMinMax[1],
    },
    yAxis: {
      min: props.options.yAxis.min,
      max: props.options.yAxis.max,
      interval: props.options.yAxis.interval,
    },
    dataset: {
      source: props.displayData
    },
    series: [{
      type: 'line',
      data: props.displayData,
      large: true,
      largeThreshold: 10000,
      progressive: 5000,
      progressiveChunkMode: 'mod',
      showSymbol: false,
      symbol: 'none',
      animation: false,
      color: '#333333',
      smooth: false,
      label: {
        show: false,
      },
      markLine: {
        animation: false,
        symbol: ['none', 'none'],
        label: {
          show: false
        },
        lineStyle: {
          type: 'solid',
          color: '#CCCCCC',
          width: 1
        },
        data: props.verticalLines
      },
      markPoint: {
        show: maxPoint.value !== undefined,
        symbol: 'triangle',
        symbolRotate: 180,
        symbolOffset: [0, -10],
        itemStyle: {
          color: 'orange'
        },
        symbolSize: MARKER_SIZE,
        data: [
          {
            coord: maxPoint.value,
          }
        ]
      },
    }]
  }
}

function resizeChart() {
  chartInstance.value?.resize()
}

function initChart() {
  if (chartRef.value) {
    chartInstance.value = echarts.init(chartRef.value, null, EChartSetting);
    chartInstance.value.setOption(getChartOptions(), false);
  }
}

function makeTooltipEvent() {
  if (tooltipEventRegistered) {
    return;
  }
  tooltipEventRegistered = true;
  chartInstance.value?.getZr().on('mousemove', (event) => {
    const point = chartInstance.value?.convertFromPixel({ seriesIndex: 0 }, [event.offsetX, event.offsetY])
    if (!point || !props.displayData[props.displayData.length - 1]) {
      return;
    }

    const [xVal] = point
    const lastX = props.displayData[props.displayData.length - 1][0];
    let nearest = null;
    if (xVal <= lastX) {
      nearest = findNearest(props.displayData, xVal);
    }

    if (!nearest) {
      tooltip.value.visible = false;
      return;
    }

    tooltip.value = {
      visible: true,
      x: event.offsetX + 10,
      y: event.offsetY + 10,
      time: formatDateTimeWithMilliseconds(new Date(nearest[0]), '/'),
      value: String(nearest[1])
    }
  })

  chartInstance.value?.getZr().on('mouseout', () => {
    tooltip.value.visible = false
  })
}

function findNearest(data: number[][], xVal: number): number[] | null {
  let left = 0, right = data.length - 1;
  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    if (data[mid][0] < xVal) left = mid + 1;
    else right = mid;
  }
  if (data[left][0] > xVal && left > 0) left--;
  return data[left];
}

async function updateChart() {
  if (!chartInstance.value) {
    return;
  }

  const isDisplayMaxPoint = props.type === 'speed' && maxPoint.value !== undefined;
  chartInstance.value.setOption({
    xAxis: {
      min: props.scaleMinMax[0],
      max: props.scaleMinMax[1]
    },
    yAxis: {
      min: props.options.yAxis.min,
      max: props.options.yAxis.max,
      interval: props.options.yAxis.interval,
    },
    series: [{
      data: props.displayData,
      markLine: {
        data: props.verticalLines
      },
      markPoint: {
        show: isDisplayMaxPoint,
        data: isDisplayMaxPoint ? [{
          coord: maxPoint.value,
        }] : []
      }
    }]
  });
}

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeChart)
  chartInstance.value?.dispose()
})

watch(() => props.scaleMinMax, async(after, before) => {
  if (after[0] === before[0] && after[1] === before[1]) {
    return;
  }

  await updateChart();
})
</script>
<template>
  <div style="position: relative;">
    <div
      ref="chartRef"
      :style="`width: 100%; height: ${props.chartHeight}px;`"
    >
    </div>
    <ToolTip
      v-if="tooltip.visible"
      :tooltip="tooltip"
    />
    <div
      v-if="!props.chartData"
      class="no-data-message"
    >
      {{ i18n.global.t('pages.every24HoursReport.preview.noData') }}
    </div>
  </div>
</template>
<style scoped>
.no-data-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #03DAC6;
  font-size: 40px;
}
</style>
